import { useQueryClient } from '@tanstack/react-query';

import { ResultListModel } from '@common-lib';

import { NewsModel, newsClient } from '@portal-clients/news';
import { NewsTypeModel } from '@portal-clients/news/news-type.model';
import { UserModel } from '@portal-clients/user';

import { useClientQuery, useInfiniteScrollClientQuery } from '@frontend/hooks';
import { useLicense } from '@frontend/licenses';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import { KeyValueStore, useOptionalUser } from '@frontend/utils';

import { useSiteUnitsQuery } from './use-site-units-query';

const fetchNews = async (
  pageToken: string | undefined,
  user: UserModel | undefined,
  siteUnitIds: string[],
) => {
  const types: NewsTypeModel[] = user
    ? ['TOP_LEVEL', 'SITE_UNIT_LEVEL']
    : ['TOP_LEVEL'];

  const response = await newsClient.search({
    pageSize: 10,
    pageToken: pageToken,
    filter: {
      siteUnitIds: siteUnitIds,
      type: types,
      period: undefined,
    },
  });

  return response;
};

const fetchNewsBySiteUnitIds = (
  siteUnitIds: string[],
  pageToken: string | undefined,
) => {
  return newsClient.search({
    pageSize: 10,
    pageToken: pageToken,
    filter: {
      siteUnitIds: siteUnitIds,
      type: ['SITE_UNIT_LEVEL'],
      period: undefined,
    },
  });
};

const fetchNewsCount = async (
  portalId: string,
  siteUnitIds: string[] | undefined,
  user: UserModel | undefined,
) => {
  const now = new Date().toISOString();

  const storedDatetime = await KeyValueStore.get(
    `portal_news_datetime_${portalId}`,
  );

  const types: NewsTypeModel[] = user
    ? ['TOP_LEVEL', 'SITE_UNIT_LEVEL']
    : ['TOP_LEVEL'];

  const response = await newsClient.count({
    pageSize: 1,
    pageToken: undefined,
    filter: {
      siteUnitIds: siteUnitIds,
      type: types,
      period: {
        from: storedDatetime ?? now,
        to: now,
      },
    },
  });

  return response;
};

export interface UseNewsProps {
  onError?(): void;
}

export interface UseCountNewsProps {
  onError?(): void;
}

export interface UseNewsBySiteUnitProps {
  siteUnitId: string;
  onError?(): void;
}

export const getNewsBySiteUnitQueryKey = (siteUnitId: string) => [
  'news-site-unit',
  siteUnitId,
];
export const getNewsQueryKey = () => ['news'];
export const getFavoriteNewsQueryKey = (siteUnitIds: string[]) => [
  'news-favorite',
  ...siteUnitIds.sort(),
];
export const getCountNewsQueryKey = () => ['news-count'];

export const useNewsBySiteUnitQuery = ({
  siteUnitId,
  onError,
}: UseNewsBySiteUnitProps) => {
  return useInfiniteScrollClientQuery({
    enabled: !!siteUnitId,
    queryKey: getNewsBySiteUnitQueryKey(siteUnitId),
    onFetch: async (pageToken) => {
      return await fetchNewsBySiteUnitIds([siteUnitId], pageToken);
    },
    onError,
  });
};

export const useNewsQuery = ({ onError }: UseNewsProps) => {
  const { user } = useOptionalUser();
  const { data: siteUnits } = useSiteUnitsQuery();
  const siteUnitIds = siteUnits?.map((siteUnit) => siteUnit.id) ?? [];

  return useInfiniteScrollClientQuery({
    queryKey: getNewsQueryKey(),
    onFetch: async (pageToken) => {
      return await fetchNews(pageToken, user, siteUnitIds);
    },
    onError,
  });
};

export const useFavoriteNewsQuery = ({ onError }: UseNewsProps) => {
  const { user } = useOptionalUser();
  const { data: siteUnits } = useSiteUnitsQuery();

  const preferredIds = user?.preferences?.sites ?? [];
  const availableIds = siteUnits?.map((siteUnit) => siteUnit.id) ?? [];
  const siteUnitIds = preferredIds.filter((id) => availableIds.includes(id));

  return useClientQuery({
    queryKey: getFavoriteNewsQueryKey(siteUnitIds),
    enabled: !!siteUnits && siteUnitIds.length > 0,
    onFetch: async () => {
      return await fetchNewsBySiteUnitIds(siteUnitIds, undefined);
    },
    onError,
  });
};

export const useCountNewsQuery = ({ onError }: UseCountNewsProps) => {
  const { user } = useOptionalUser();
  const { portalId, features } = usePortalPublicConfiguration();
  const hasLicense = useLicense('news');

  return useClientQuery({
    queryKey: getCountNewsQueryKey(),
    async onFetch() {
      return await fetchNewsCount(portalId, user?.preferences?.sites, user);
    },
    enabled: hasLicense && features?.news?.enabled,
    onError,
  });
};

export const usePrefetchNewsQuery = () => {
  const { user } = useOptionalUser();
  const { data: siteUnits } = useSiteUnitsQuery();
  const queryClient = useQueryClient();
  const hasLicense = useLicense('news');
  const { features, portalId } = usePortalPublicConfiguration();
  const siteUnitIds = siteUnits?.map((siteUnit) => siteUnit.id) ?? [];

  if (!hasLicense || !features?.news?.enabled) {
    return;
  }

  queryClient.prefetchInfiniteQuery({
    pages: 1,
    queryKey: getNewsQueryKey(),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: ResultListModel<NewsModel>) => {
      return lastPage.nextPageToken;
    },
    queryFn: async ({ pageParam: pageToken }) => {
      const response = await fetchNews(pageToken, user, siteUnitIds);
      return response;
    },
  });

  queryClient.prefetchQuery({
    queryKey: getCountNewsQueryKey(),
    queryFn: async () => {
      return await fetchNewsCount(portalId, user?.preferences?.sites, user);
    },
  });

  if (!user || !user?.preferences?.sites?.length) {
    return;
  }

  for (const id of user.preferences.sites) {
    queryClient.prefetchInfiniteQuery({
      pages: 1,
      queryKey: getNewsBySiteUnitQueryKey(id),
      initialPageParam: undefined,
      getNextPageParam: (lastPage: ResultListModel<NewsModel>) => {
        return lastPage.nextPageToken;
      },
      queryFn: async ({ pageParam: pageToken }) => {
        const response = await fetchNewsBySiteUnitIds([id], pageToken);
        return response;
      },
    });
  }
};
